import { Link, Stack, Typography } from "@mui/material";
import { useUser } from "@shuttlerock/auth";
import { SrDialog } from "@shuttlerock/mui-components";
import {
  closeTrackingPrompt,
  openTrackingPrompt,
  setTrackingConsent,
  useTrackingConsent,
} from "@shuttlerock/observability";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect } from "react";
import { useTranslation } from "../../i18n";
import { PRIVACY_POLICY_URL } from "./privacy-policy-url";

/**
 * Asks a signed-in user whether product analytics may run, whenever the store
 * says the prompt is due. Analytics stays off until they allow it; the two
 * answers are styled identically so neither is nudged.
 */
export function TrackingConsentPrompt() {
  const { t } = useTranslation("preferences");
  const { user } = useUser();
  const principalKey = user?.principalKey;
  const { trackingConsent } = useFlags();
  const { promptDue } = useTrackingConsent(principalKey);
  // Until the flag is known the prompt neither opens nor counts as shown.
  const open = trackingConsent === true && promptDue;

  useEffect(() => {
    if (principalKey && open) openTrackingPrompt(principalKey);
  }, [principalKey, open]);

  if (!principalKey) return null;

  const answer = (allowed: boolean) => setTrackingConsent(principalKey, allowed);

  return (
    <SrDialog
      name="TrackingConsent"
      open={open}
      maxWidth="xs"
      withCloseButton
      closeButtonAriaLabel={t("privacy:prompt:close")}
      onClose={() => closeTrackingPrompt(principalKey)}
    >
      <SrDialog.Title>{t("privacy:prompt:title")}</SrDialog.Title>
      <SrDialog.Content>
        <Stack gap={1.5}>
          <Typography variant="body2" color="text.secondary">
            {t("privacy:prompt:purpose")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("privacy:prompt:assurance")}{" "}
            <Link href={PRIVACY_POLICY_URL} target="_blank" rel="noopener">
              {t("privacy:prompt:learnMore")}
            </Link>
          </Typography>
        </Stack>
      </SrDialog.Content>
      <SrDialog.Actions>
        <SrDialog.Action name="Deny" color="secondary" onClick={() => answer(false)}>
          {t("privacy:prompt:deny")}
        </SrDialog.Action>
        <SrDialog.Action name="Allow" color="secondary" onClick={() => answer(true)}>
          {t("privacy:prompt:allow")}
        </SrDialog.Action>
      </SrDialog.Actions>
    </SrDialog>
  );
}
